import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type BracketSlot, type BracketShot, type BracketSide } from '../lib/api';
import { ImageDropzone } from './ImageDropzone';
import { Check, Pencil, X, Camera, AlertTriangle, ScanLine } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// BracketCanvas — the bracket rendering shared by the authed Bracket page and
// the public /watch spectator page. All interactive affordances (score entry,
// screenshot upload, OCR proposals) are gated behind `canEnter`; with it false
// this renders a pure read-only bracket.
// ─────────────────────────────────────────────────────────────────────────────

function sourceLabel(slot: BracketSlot, side: 'A' | 'B'): string {
  const bye = side === 'A' ? slot.byeA : slot.byeB;
  if (bye) return 'BYE';
  const src = side === 'A' ? slot.sourceA : slot.sourceB;
  if (src.kind === 'WINNER_OF') return `Winner of ${src.slot}`;
  if (src.kind === 'LOSER_OF') return `Loser of ${src.slot}`;
  return 'TBD';
}

function ScoreEntry({ compId, slot, prefill, onClose }: {
  compId: string; slot: BracketSlot; prefill?: { a: number; b: number }; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [scoreA, setScoreA] = useState<string>(prefill?.a.toString() ?? slot.scoreA?.toString() ?? '');
  const [scoreB, setScoreB] = useState<string>(prefill?.b.toString() ?? slot.scoreB?.toString() ?? '');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => api.submitBracketResult(compId, slot.slot, Number(scoreA), Number(scoreB)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bracket', compId] });
      onClose();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const valid = scoreA !== '' && scoreB !== '' && Number(scoreA) !== Number(scoreB);

  return (
    <div className="border-t border-gray-700/40 px-3 py-2">
      <div className="flex items-center gap-2">
        <input
          type="number" min={0} value={scoreA} onChange={e => setScoreA(e.target.value)}
          placeholder={slot.teamA} aria-label={`${slot.teamA} score`}
          className="w-16 rounded border border-gray-700/50 bg-surface-900 px-2 py-1 text-xs text-white"
        />
        <span className="text-[10px] text-gray-500">vs</span>
        <input
          type="number" min={0} value={scoreB} onChange={e => setScoreB(e.target.value)}
          placeholder={slot.teamB} aria-label={`${slot.teamB} score`}
          className="w-16 rounded border border-gray-700/50 bg-surface-900 px-2 py-1 text-xs text-white"
        />
        <button
          onClick={() => mut.mutate()} disabled={!valid || mut.isPending}
          className="rounded bg-violet-600 p-1.5 text-white transition-colors hover:bg-violet-500 disabled:opacity-40"
          title="Save final score"
        >
          <Check className="h-3 w-3" />
        </button>
        <button onClick={onClose} className="rounded p-1.5 text-gray-500 hover:text-gray-300" title="Cancel">
          <X className="h-3 w-3" />
        </button>
      </div>
      {scoreA !== '' && scoreA === scoreB && (
        <p className="mt-1 text-[10px] text-amber-400">Elimination matches can't tie.</p>
      )}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

// Screenshot → presigned PUT → confirm → OCR runner extracts header totals.
function ShotUploadButton({ compId, slot, onError }: { compId: string; slot: number; onError: (msg: string) => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    onError('');
    try {
      const ct = file.type === 'image/png' || file.type === 'image/webp' ? file.type : 'image/jpeg';
      const { url, s3Key } = await api.bracketShotUploadUrl(compId, slot, ct);
      const put = await fetch(url, { method: 'PUT', headers: { 'Content-Type': ct }, body: file });
      if (!put.ok) throw new Error(`Screenshot upload failed (${put.status})`);
      await api.bracketShotConfirm(compId, slot, s3Key);
      qc.invalidateQueries({ queryKey: ['bracket', compId] });
    } catch (e) {
      onError(parseApiError(e).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    // Wrap mode — drag a screenshot straight onto the camera icon, same
    // onFile the click path already uses; no layout change.
    <ImageDropzone onFile={file => onFile(file)} accept="image/png,image/jpeg,image/webp" disabled={busy} className="inline-block">
      <input
        ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={e => onFile(e.target.files?.[0])}
      />
      <button
        onClick={() => fileRef.current?.click()} disabled={busy}
        className="rounded bg-surface-700 p-1 text-gray-400 transition-colors hover:text-violet-300 disabled:opacity-40"
        title="Upload scoreboard screenshot — OCR grabs the totals, or drag it here"
      >
        <Camera className="h-3 w-3" />
      </button>
    </ImageDropzone>
  );
}

function SlotCard({ compId, slot, shots, canEnter }: {
  compId: string; slot: BracketSlot; shots: BracketShot[]; canEnter: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [prefill, setPrefill] = useState<{ a: number; b: number } | undefined>();
  const [shotError, setShotError] = useState('');
  const played = slot.status === 'PLAYED';
  const scoreable = slot.status === 'READY' || played;
  const pendingShots = shots.filter(s => s.status === 'UPLOADED' || s.status === 'OCR_PROCESSING').length;
  const lastFailed = [...shots].reverse().find(s => s.status === 'FAILED');
  const proposal = slot.proposed;

  const row = (side: 'A' | 'B') => {
    const team = side === 'A' ? slot.teamA : slot.teamB;
    const score = side === 'A' ? slot.scoreA : slot.scoreB;
    const isWinner = played && team !== undefined && team === slot.winner;
    return (
      <div className={`flex items-center justify-between gap-2 px-3 py-1.5 ${isWinner ? 'bg-accent-500/10' : ''}`}>
        <span className={`truncate text-xs ${team ? (isWinner ? 'font-semibold text-accent-400' : 'text-gray-200') : 'italic text-gray-600'}`}>
          {team ?? sourceLabel(slot, side)}
        </span>
        {score !== undefined && (
          <span className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-xs ${isWinner ? 'bg-accent-500 text-white' : 'bg-surface-700 text-gray-400'}`}>
            {score}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`w-48 rounded-lg border bg-surface-900 ${
      slot.status === 'READY' ? 'border-violet-500/40' : 'border-gray-700/40'
    } ${slot.status === 'BYE' ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between border-b border-gray-700/40 px-3 py-1">
        <span className="font-mono text-[10px] text-gray-500">#{slot.slot}</span>
        <span className="flex items-center gap-1">
          {scoreable && canEnter && !editing && (
            <ShotUploadButton compId={compId} slot={slot.slot} onError={setShotError} />
          )}
          {slot.status === 'READY' && !editing && canEnter && (
            <button
              onClick={() => { setPrefill(undefined); setEditing(true); }}
              className="rounded bg-violet-600/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-300 hover:bg-violet-600/40"
            >
              Enter score
            </button>
          )}
          {played && canEnter && !editing && (
            <button onClick={() => { setPrefill(undefined); setEditing(true); }} className="rounded p-0.5 text-gray-600 hover:text-gray-400" title="Correct score">
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {slot.status === 'BYE' && <span className="text-[10px] uppercase text-gray-600">bye</span>}
          {slot.status === 'READY' && !canEnter && <span className="text-[10px] uppercase text-violet-400">up next</span>}
        </span>
      </div>
      <div className="divide-y divide-gray-700/30">
        {row('A')}
        {row('B')}
      </div>
      {pendingShots > 0 && (
        <div className="flex items-center gap-1.5 border-t border-gray-700/40 px-3 py-1.5">
          <ScanLine className="h-3 w-3 animate-pulse text-violet-400" />
          <span className="text-[10px] text-violet-300">Reading screenshot{pendingShots > 1 ? 's' : ''}…</span>
        </div>
      )}
      {(shotError || (lastFailed?.ocrError && pendingShots === 0 && !proposal)) && canEnter && (
        <p className="border-t border-gray-700/40 px-3 py-1.5 text-[10px] text-red-400">
          {shotError || lastFailed?.ocrError}
        </p>
      )}
      {proposal && slot.status === 'READY' && !editing && canEnter && (
        <div className="border-t border-gray-700/40 px-3 py-1.5">
          <p className="text-[10px] text-gray-400">
            OCR proposal:{' '}
            <span className="font-mono font-semibold text-violet-300">{proposal.scoreA}–{proposal.scoreB}</span>
            {' '}({proposal.shots} shot{proposal.shots !== 1 ? 's' : ''}
            {proposal.perShot.some(s => s.gameMode) ? ` · ${proposal.perShot.map(s => s.gameMode).filter(Boolean).join(', ')}` : ''})
          </p>
          {!proposal.orientationConfident && (
            <p className="mt-0.5 flex items-center gap-1 text-[10px] text-amber-400">
              <AlertTriangle className="h-3 w-3" /> Couldn't match team names — double-check which side is which.
            </p>
          )}
          <button
            onClick={() => { setPrefill({ a: proposal.scoreA, b: proposal.scoreB }); setEditing(true); }}
            className="mt-1 rounded bg-violet-600/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-300 hover:bg-violet-600/40"
          >
            Use proposal
          </button>
        </div>
      )}
      {editing && <ScoreEntry compId={compId} slot={slot} prefill={prefill} onClose={() => setEditing(false)} />}
    </div>
  );
}

export function SideRounds({ compId, slots, shots, side, canEnter }: {
  compId: string; slots: BracketSlot[]; shots: BracketShot[]; side: BracketSide; canEnter: boolean;
}) {
  const rounds = useMemo(() => {
    const bySide = slots.filter(s => s.side === side);
    const map = new Map<number, BracketSlot[]>();
    for (const s of bySide) {
      const list = map.get(s.round) ?? [];
      list.push(s);
      map.set(s.round, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [slots, side]);

  if (rounds.length === 0) return null;

  return (
    <div className="flex items-stretch gap-6 overflow-x-auto pb-2">
      {rounds.map(([round, roundSlots]) => (
        <div key={round} className="flex shrink-0 flex-col">
          <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {roundSlots[0].roundLabel}
          </p>
          <div className="flex flex-1 flex-col justify-around gap-3">
            {roundSlots.sort((a, b) => a.slot - b.slot).map(s => (
              <SlotCard key={s.slot} compId={compId} slot={s} shots={shots.filter(sh => sh.slot === s.slot)} canEnter={canEnter} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
